import {
  LocationState,
  LocationDescriptorObject,
} from 'history';

import { TransferActionType } from '@app/common/types/enum/transferActionType';

import {
  ChannelId,
  PaymentOptionType,
} from '@make-transfer/types/common';
import { ExternalTransferFieldNames } from '@make-transfer/types/external';
import { InternalTransferFormField } from '@make-transfer/types/internal';

import { getChannelNewPaymentRoute, getChannelSendNewRoute } from './routing';

class ActionNavigator {
  // state cannot be typed at the moment
  getActionRoute = (state: any): LocationDescriptorObject => {
    if (!state) {
      return {
        pathname: getChannelNewPaymentRoute(ChannelId.Internal),
      };
    }

    switch (state.transferType) {
      case TransferActionType.SHAREFROM: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            from: state.accountId,
          },
        };
      }
      case TransferActionType.SHARETO: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
          },
        };
      }
      case TransferActionType.LOANREGULAR: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            paymentOption: PaymentOptionType.RegularPayment,
          },
        };
      }
      case TransferActionType.LOANPAYOFF: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            paymentOption: PaymentOptionType.BalancePayoff,
          },
        };
      }
      case TransferActionType.LOANPRINCIPALONLY: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            paymentOption: PaymentOptionType.PrincipalOnly,
          },
        };
      }
      case TransferActionType.LOANSKIP: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Skip),
          state: {
            from: state.accountId,
          },
        };
      }
      case TransferActionType.LOANMINIMUM: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            paymentOption: PaymentOptionType.Minimum,
          },
        };
      }
      case TransferActionType.LOANLASTSTATEMENT: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            paymentOption: PaymentOptionType.LastStatement,
          },
        };
      }
      case TransferActionType.LOANFROM: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            from: state.accountId,
          },
        };
      }
      case TransferActionType.LOANCUSTOM: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            to: state.accountId,
            amount: state.amount,
            paymentOption: PaymentOptionType.Default,
          },
        };
      }
      case TransferActionType.M2MTO: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.M2M),
          state: {
            to: state.accountId,
          },
        };
      }
      case TransferActionType.STOPCHECK: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.StopCheck),
          state: {
            accountId: state.accountId,
          },
        };
      }
      case TransferActionType.FUNDSHARE_EXT: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.External),
          state: {
            ...state,
            [ExternalTransferFieldNames.AMOUNT]: state.amount,
            [ExternalTransferFieldNames.FROM]: state.from,
            [ExternalTransferFieldNames.MEMO]: state.memo,
            [ExternalTransferFieldNames.TO]: state.to,
          },
        };
      }
      case TransferActionType.FUNDSHARE_INT: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
          state: {
            ...state,
            [InternalTransferFormField.AMOUNT]: state.amount,
            [InternalTransferFormField.FROM]: state.from,
            [InternalTransferFormField.MEMO]: state.memo,
            [InternalTransferFormField.TO]: state.to,
          },
        };
      }
      case TransferActionType.LINKEXTACC: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.External),
          state: {
            ...state,
          },
        };
      }
      case TransferActionType.FROMTEMPLATE: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.ACH),
          state: {
            fromTemplateId: state.templateId,
            contextUserId: state.contextUserId,
          },
        };
      }
      case TransferActionType.TOTEMPLATE: {
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.ACH),
          state: {
            toTemplateId: state.templateId,
            contextUserId: state.contextUserId,
          },
        };
      }
      case TransferActionType.TORECIPIENT_ONEOFFINVOICE: {
        return {
          pathname: getChannelSendNewRoute(ChannelId.Invoices),
          state: {
            toRecipientId: state.toRecipientId,
            contextUserId: state.contextUserId,
          },
        };
      }
      default:
        return {
          pathname: getChannelNewPaymentRoute(ChannelId.Internal),
        };
    }
  };

  isActionLink = (location: LocationState) => {
    const transferType = (location as any)?.transferType;
    return (transferType ?? false) && Object.values(TransferActionType).includes(transferType);
  };
}

export const ActionNavigatorService = new ActionNavigator();
